// The two fixed engagement teams and the helpers that split the roster.
// Team assignment is intentionally kept client-side (no DB write): the split is
// "purely random each time" and driven from the presenter's screen, so a plain
// shuffle + even alternate deal is all that's needed.

export const TEAM_IDS = ['vikings', 'panthers'];

// Where the Team Games page persists the current teams + scores (client-only).
export const TEAM_GAMES_STORAGE_KEY = 'planner:teamgames:v1';

// Wipe the saved teams/scores so a fresh sign-in starts from scratch (must
// reshuffle). Called on logout.
export function resetTeamGamesState() {
  try {
    if (typeof localStorage !== 'undefined') localStorage.removeItem(TEAM_GAMES_STORAGE_KEY);
  } catch { /* storage unavailable — nothing to clear */ }
}

// The two captains (Venkat, Prakash) and the host (Srinivas) run the games and
// are NOT dealt onto either team. Matched by email or by first name so it works
// whether or not the attendee records carry these exact addresses.
export const TEAM_EXCLUDED_EMAILS = [
  'venkat@nativeorange.ai',
  'prakash@nativeorange.ai',
  'srinivase@nativeorange.ai',
];
export const TEAM_EXCLUDED_FIRST_NAMES = ['venkat', 'prakash', 'srinivas'];

export function isExcludedFromTeams(member = {}) {
  const email = (member.email || '').toLowerCase();
  const firstName = (member.name || '').trim().split(/\s+/)[0].toLowerCase();
  return TEAM_EXCLUDED_EMAILS.includes(email) || TEAM_EXCLUDED_FIRST_NAMES.includes(firstName);
}

// A member matches a key when their first name OR their email local-part is an
// exact match. Both are checked because the company writes surnames as an
// initial ("Sameer K" → sameerk@), so an attendee record may carry either form
// — "Saptarshi C", "Saptarshic", or nothing but the address.
function matchesKeys(member = {}, keys = []) {
  const firstName = (member.name || '').trim().split(/\s+/)[0].toLowerCase();
  const emailLocal = (member.email || '').toLowerCase().split('@')[0];
  return keys.includes(firstName) || (Boolean(emailLocal) && keys.includes(emailLocal));
}

// People who are not taking part in the games at all. Unlike the captains and
// host above they are NOT surfaced as "hosting" either — they simply do not
// appear anywhere in Team Games. Removing a name here puts them straight back
// in the draw; nothing is deleted from the attendee list.
export const TEAM_SITTING_OUT_KEYS = [
  'sanket',
  'saptarshi', 'saptarshic',
  'sameer', 'sameerk',
];

export function isSittingOut(member = {}) {
  return matchesKeys(member, TEAM_SITTING_OUT_KEYS);
}

// The host runs the games; the captains front a team each. All three sit out of
// the draw (TEAM_EXCLUDED_* above), but they are surfaced very differently —
// the host gets his own reveal card, each captain is badged onto their team.
export const TEAM_HOST_KEYS = ['srinivas', 'srinivase'];
export const TEAM_CAPTAIN_KEYS = {
  vikings: ['venkat'],
  panthers: ['prakash'],
};

export function isHost(member = {}) {
  return matchesKeys(member, TEAM_HOST_KEYS);
}

// Which team this person captains, or null if they captain neither.
export function captainTeamOf(member = {}) {
  return TEAM_IDS.find((teamId) => matchesKeys(member, TEAM_CAPTAIN_KEYS[teamId])) || null;
}

// Pairs who must never be dealt onto the same team. Each entry is two key
// lists, matched the same way as the sit-out list above.
export const TEAM_KEEP_APART = [
  [['arya', 'aryay'], ['nutan', 'nutank']],
];

function isKeptApart(member) {
  return TEAM_KEEP_APART.some(([a, b]) => matchesKeys(member, a) || matchesKeys(member, b));
}

function locate(teams, keys) {
  for (const teamId of TEAM_IDS) {
    const idx = teams[teamId].findIndex((m) => matchesKeys(m, keys));
    if (idx >= 0) return { teamId, idx, member: teams[teamId][idx] };
  }
  return null;
}

// Split up anyone on the keep-apart list who happened to land together.
//
// Applied AFTER the random deal rather than by constraining the deal itself:
// if both ended up on one side, one of them trades places with a random player
// from the other side. That keeps both team sizes exactly as dealt, and leaves
// everyone else's assignment untouched and still uniformly random.
export function enforceKeepApart(teams) {
  const next = { vikings: [...teams.vikings], panthers: [...teams.panthers] };

  for (const [keysA, keysB] of TEAM_KEEP_APART) {
    const a = locate(next, keysA);
    const b = locate(next, keysB);
    if (!a || !b) continue;                 // one of them is not playing
    if (a.teamId !== b.teamId) continue;    // already on opposite sides

    const from = b.teamId;
    const to = from === 'vikings' ? 'panthers' : 'vikings';
    // Never trade with someone who is themselves under a keep-apart rule —
    // that would just recreate the clash we are resolving.
    const candidates = next[to]
      .map((member, idx) => ({ member, idx }))
      .filter(({ member }) => !isKeptApart(member));
    if (!candidates.length) continue;       // nobody safe to swap with

    const pick = candidates[Math.floor(Math.random() * candidates.length)];
    next[to][pick.idx] = b.member;
    next[from][b.idx] = pick.member;
  }

  return next;
}

export const TEAM_META = {
  vikings:  { id: 'vikings',  name: 'Venkat Vikings',   short: 'Vikings',  color: '#2563eb', soft: 'rgba(37,99,235,0.10)' },
  panthers: { id: 'panthers', name: 'Prakash Panthers', short: 'Panthers', color: '#7c3aed', soft: 'rgba(124,58,237,0.10)' },
};

// Fisher–Yates shuffle — returns a new array, never mutates the input.
export function shuffle(list) {
  const a = [...list];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Randomly deal members into two even teams (odd member lands on Vikings).
export function splitTeams(members) {
  const shuffled = shuffle(members);
  const teams = { vikings: [], panthers: [] };
  shuffled.forEach((m, i) => {
    teams[i % 2 === 0 ? 'vikings' : 'panthers'].push(m);
  });
  return teams;
}

// Decide the teams, then hand back a reveal order that is shuffled AGAIN,
// independently of the assignment.
//
// This matters: splitTeams deals strictly alternately, so revealing people in
// dealt order would go Vikings, Panthers, Vikings, Panthers… and the room works
// out the pattern by the third pick — which kills the suspense the draft exists
// for. Re-shuffling only the order leaves every assignment untouched, so the
// split stays exactly as even and as random as splitTeams made it.
export function buildDraftQueue(members) {
  const teams = enforceKeepApart(splitTeams(members));
  const queue = shuffle([
    ...teams.vikings.map((member) => ({ member, teamId: 'vikings' })),
    ...teams.panthers.map((member) => ({ member, teamId: 'panthers' })),
  ]);
  return { teams, queue };
}
